<template>
  <div class="metro-toilet-chart-container">
    <div ref="container" class="metro-toilet-chart"></div>
    <!-- 数据来源注释 -->
    <div class="data-source">
      数据来源：上海地铁官方网站
    </div>
  </div>
</template>

<script lang="ts" setup>
import * as d3 from 'd3';
import { ref, onMounted, watch, onBeforeUnmount } from 'vue';

interface ToiletData {
  line: string;
  stations: number;
  totalToilets: number;
  insideToilets: number;
  outsideToilets: number;
  bothAreasToilets: number;
  externalToilets: number;
  transferIssues: number;
  specificStations?: string;
  lineColor?: string;
}

const props = defineProps<{
  data: ToiletData[];
  color?: string;
}>();

const container = ref<HTMLDivElement | null>(null);
let resizeObserver: ResizeObserver | null = null;
let resizeTimer: number | null = null;

// 地铁线路颜色映射
const metroColors: { [key: string]: string } = {
  '1号线': '#E3002B',
  '2号线': '#82BF25',
  '3号线': '#FCD600',
  '4号线': '#461D84',
  '5号线': '#944D9A',
  '6号线': '#D40068',
  '7号线': '#ED6F00',
  '8号线': '#0094D8',
  '9号线': '#87CAED',
  '10号线': '#C6AFD4',
  '11号线': '#871C2B',
  '12号线': '#007A60',
  '13号线': '#E999C0',
  '14号线': '#626020',
  '15号线': '#BCA886',
  '16号线': '#98D1C0',
  '17号线': '#BC796F',
  '18号线': '#C4984F',
  '浦江线': '#B5B5B6',
  '市域机场线': '#008B9A'
};

function render() {
  if (!container.value || !props.data.length) return;
  
  const element = container.value;
  const width = element.clientWidth;
  const height = 500; // 增加高度
    const margin = { top: 60, right: 40, bottom: 100, left: 90 }; // 增加边距
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;
  
  element.innerHTML = '';
  
  const svg = d3.select(element)
    .append('svg')
    .attr('width', width)
    .attr('height', height);
  
  const g = svg.append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`);
  
  // 数据处理 - 计算卫生间覆盖率
  const processedData = props.data.map(d => ({
    ...d,
    coverageRate: (d.bothAreasToilets / d.stations) * 100,
    toiletDensity: d.totalToilets / d.stations,
    lineColor: metroColors[d.line] || '#6b7280'
  })).filter(d => d.line !== '浦江线' && d.line !== '市域机场线'); // 过滤特殊线路
  
  // 比例尺
  const xScale = d3.scaleBand()
    .domain(processedData.map(d => d.line))
    .range([0, innerWidth])
    .padding(0.3); // 增加间距
  
  const yScale = d3.scaleLinear()
    .domain([0, Math.max(100, d3.max(processedData, d => d.coverageRate) || 0)])
    .range([innerHeight, 0])
    .nice(); // 使刻度更整齐
  
  const toiletScale = d3.scaleLinear()
    .domain([0, d3.max(processedData, d => d.toiletDensity) || 1])
    .range([8, 25]); // 调整气泡大小范围
  
  // 背景网格线
  const yTicks = yScale.ticks(6);
  yTicks.forEach(tick => {
    g.append('line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', yScale(tick))
      .attr('y2', yScale(tick))
      .attr('stroke', '#f1f5f9')
      .attr('stroke-width', 1)
      .attr('opacity', 0.8);
  });
  
  // 绘制气泡
  const bubbles = g.selectAll('.bubble')
    .data(processedData)
    .enter()
    .append('g')
    .attr('class', 'bubble')
    .attr('transform', d => `translate(${(xScale(d.line) || 0) + xScale.bandwidth() / 2}, ${yScale(d.coverageRate)})`);
  
  // 主气泡（总卫生间数据）- 使用地铁线路颜色
  bubbles.append('circle')
    .attr('r', 0)
    .attr('fill', d => d.lineColor)
    .attr('stroke', '#ffffff')
    .attr('stroke-width', 3)
    .attr('opacity', 0.85)
    .style('cursor', 'pointer')
    .style('filter', 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15))')
    .on('mouseenter', function(event, d) {
      d3.select(this)
        .transition()
        .duration(200)
        .attr('stroke-width', 4)
        .attr('opacity', 1)
        .attr('transform', 'scale(1.15)')
        .style('filter', 'drop-shadow(0 6px 12px rgba(0, 0, 0, 0.25))');
      
      showTooltip(d, this);
    })
    .on('mouseleave', function() {
      d3.select(this)
        .transition()
        .duration(200)
        .attr('stroke-width', 3)
        .attr('opacity', 0.85)
        .attr('transform', 'scale(1)')
        .style('filter', 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15))');
      
      hideTooltip();
    })
    .transition()
    .duration(1000)
    .delay((d, i) => i * 100)
    .attr('r', d => toiletScale(d.toiletDensity));
  
  // 内部小圆圈（费区内卫生间）- 使用相同颜色的更深色调
  bubbles.append('circle')
    .attr('r', 0)
    .attr('fill', d => d3.color(d.lineColor)?.darker(0.5)?.toString() || '#ffffff')
    .attr('opacity', 0.9)
    .style('pointer-events', 'none')  // 让鼠标事件穿透到主圆圈
    .transition()
    .duration(800)
    .delay((d, i) => i * 100 + 200)
    .attr('r', d => toiletScale(d.toiletDensity) * 0.5);

  // 覆盖率数值标签
  bubbles.append('text')
    .attr('text-anchor', 'middle')
    .attr('dominant-baseline', 'middle')
    .attr('font-size', '10px')
    .attr('font-weight', '700')
    .attr('fill', '#ffffff')
    .style('opacity', 0)
    .style('text-shadow', '1px 1px 2px rgba(0, 0, 0, 0.5)')
    .style('pointer-events', 'none')  // 让鼠标事件穿透到下面的圆圈
    .text(d => `${Math.round(d.coverageRate)}%`)
    .transition()
    .duration(500)
    .delay((d, i) => i * 100 + 1200)
    .style('opacity', 1);
  
  // Y轴
  const yAxis = d3.axisLeft(yScale)
    .tickFormat(d => `${d}%`)
    .tickSize(-innerWidth)
    .tickPadding(15);
  
  g.append('g')
    .attr('class', 'y-axis')
    .call(yAxis)
    .selectAll('text')
    .attr('font-size', '12px')
    .attr('fill', '#6b7280');
  
  g.select('.y-axis').selectAll('line')
    .attr('stroke', '#e5e7eb')
    .attr('opacity', 0.3);
  
  g.select('.y-axis').select('.domain').remove();
  
  // X轴
  const xAxis = d3.axisBottom(xScale)
    .tickFormat(d => d)
    .tickSizeOuter(0);
  
  const xAxisGroup = g.append('g')
    .attr('class', 'x-axis')
    .attr('transform', `translate(0, ${innerHeight})`)
    .call(xAxis);
    
  // X轴标签样式 - 使用对应线路颜色
  xAxisGroup.selectAll('text')
    .attr('font-size', '12px')
    .attr('font-weight', '600')
    .attr('fill', d => {
      const lineData = processedData.find(item => item.line === d);
      return lineData ? lineData.lineColor : '#374151';
    })
    .attr('transform', 'rotate(-45)')
    .style('text-anchor', 'end')
    .attr('dx', '-0.5em')
    .attr('dy', '0.15em');
  
  // X轴线条样式
  xAxisGroup.select('.domain')
    .attr('stroke', '#e5e7eb')
    .attr('stroke-width', 1);
    
  xAxisGroup.selectAll('.tick line')
    .attr('stroke', '#e5e7eb')
    .attr('opacity', 0.5);
  
  // Y轴标题
  g.append('text')
    .attr('transform', 'rotate(-90)')
    .attr('y', -70)
    .attr('x', -innerHeight / 2)
    .attr('text-anchor', 'middle')
    .attr('font-size', '14px')
    .attr('font-weight', '600')
    .attr('fill', '#374151')
    .text('卫生间覆盖率 (%)');
  
  // 图例 - 居中靠上
  const legend = g.append('g')
    .attr('transform', `translate(${innerWidth / 2}, 5)`);
  
  // 主气泡图例
  const legendMain = legend.append('g')
    .attr('transform', 'translate(-80, 0)');
  
  legendMain.append('circle')
    .attr('r', 12)
    .attr('fill', '#3b82f6')
    .attr('stroke', '#ffffff')
    .attr('stroke-width', 3)
    .attr('opacity', 0.85);
  
  legendMain.append('text')
    .attr('x', -20)
    .attr('y', 0)
    .attr('dominant-baseline', 'middle')
    .attr('text-anchor', 'end')  // 右对齐
    .attr('font-size', '12px')
    .attr('fill', '#374151')
    .text('总卫生间');
  
  // 内部圆圈图例
  const legendInner = legend.append('g')
    .attr('transform', 'translate(80, 0)');
  
  legendInner.append('circle')
    .attr('r', 8)
    .attr('fill', '#1e40af')
    .attr('opacity', 0.9);
  
  legendInner.append('text')
    .attr('x', -15)
    .attr('y', 0)
    .attr('dominant-baseline', 'middle')
    .attr('text-anchor', 'end')  // 右对齐
    .attr('font-size', '12px')
    .attr('fill', '#374151')
    .text('费区内卫生间');
  
  // 提示框函数
  function showTooltip(data: any, element: any) {
    const tooltip = d3.select('body').selectAll('.toilet-tooltip').data([0]);
    const tooltipEnter = tooltip.enter().append('div')
      .attr('class', 'toilet-tooltip')
      .style('position', 'absolute')
      .style('background', 'rgba(30, 41, 59, 0.95)')
      .style('color', 'white')
      .style('padding', '16px')
      .style('border-radius', '12px')
      .style('font-size', '13px')
      .style('pointer-events', 'none')
      .style('opacity', 0)
      .style('backdrop-filter', 'blur(10px)')
      .style('box-shadow', '0 8px 25px rgba(0, 0, 0, 0.15)')
      .style('z-index', '1000')
      .style('border', '1px solid rgba(255, 255, 255, 0.1)')
      .style('max-width', '280px');
    
    const rect = element.getBoundingClientRect();
    tooltipEnter.merge(tooltip as any)
      .html(`
        <div style="font-weight: 600; margin-bottom: 8px; color: ${data.lineColor}; font-size: 14px;">
          ${data.line}
        </div>
        <div style="display: grid; gap: 4px; color: #cbd5e1; font-size: 12px;">
          <div>总站数: <span style="color: white; font-weight: 600;">${data.stations}站</span></div>
          <div>总卫生间数: <span style="color: white; font-weight: 600;">${data.totalToilets}个</span></div>
          <div>费区内卫生间: <span style="color: white; font-weight: 600;">${data.insideToilets}个</span></div>
          <div>费区外卫生间: <span style="color: white; font-weight: 600;">${data.outsideToilets}个</span></div>
          <div>覆盖率: <span style="color: ${data.lineColor}; font-weight: 600;">${Math.round(data.coverageRate)}%</span></div>
          <div>平均密度: <span style="color: white; font-weight: 600;">${data.toiletDensity.toFixed(1)}个/站</span></div>
          ${data.transferIssues > 0 ? `<div style="color: #fbbf24;">卫生间不在该站台的可换乘站: ${data.transferIssues}个</div>` : ''}
          ${data.specificStations ? `<div style="color: #94a3b8; margin-top: 6px; font-size: 11px;">具体站点: ${data.specificStations}</div>` : ''}
        </div>
      `)
      .style('opacity', 1)
      .style('left', (rect.left + window.scrollX) + 'px')
      .style('top', (rect.top + window.scrollY - 10) + 'px')
      .style('transform', 'translate(-50%, -100%)');
  }
  
  function hideTooltip() {
    d3.select('.toilet-tooltip')
      .transition()
      .duration(200)
      .style('opacity', 0);
  }
}

onMounted(() => {
  render();
  if (container.value) {
    resizeObserver = new ResizeObserver(() => {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => render(), 100);
    });
    resizeObserver.observe(container.value);
  }
});

watch(() => props.data, () => render(), { deep: true });

onBeforeUnmount(() => {
  if (resizeObserver && container.value) resizeObserver.unobserve(container.value);
  if (resizeTimer) window.clearTimeout(resizeTimer);
});
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Noto+Serif+SC:wght@400;500;600;700&display=swap');

.metro-toilet-chart-container {
  position: relative;
  width: 100%;
  height: 500px;
}

/* 数据来源注释 */
.data-source {
  position: absolute;
  bottom: 12px;
  right: 16px;
  font-size: 12px;
  color: #64748b;
  background: rgba(255, 255, 255, 0.9);
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid rgba(100, 116, 139, 0.2);
  backdrop-filter: blur(4px);
  z-index: 10;
  font-family: 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
  font-weight: 400;
  letter-spacing: 0.2px;
}

.metro-toilet-chart {
  width: 100%;
  height: 500px; /* 增加高度 */
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(226, 232, 240, 0.8);
}

:deep(.toilet-tooltip) {
  font-family: 'Noto Serif SC', serif;
}
</style>